import { ExternalLink, FileText, Zap, Shield, BookOpen, ArrowRight, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border-b border-slate-200 last:border-b-0">
    <button
      onClick={onClick}
      className="w-full py-5 flex items-center justify-between text-left focus:outline-none group"
      data-testid={`faq-question-${question.toLowerCase().replace(/\s+/g, '-').slice(0, 30)}`}
    >
      <span 
        className="text-lg font-medium text-slate-900 group-hover:text-teal-700 transition-colors pr-4"
        style={{ fontFamily: 'Outfit, sans-serif' }}
      >
        {question}
      </span>
      <ChevronDown 
        className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
      />
    </button>
    <div 
      className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-96 pb-5' : 'max-h-0'}`}
    >
      <p className="text-slate-600 leading-relaxed">{answer}</p>
    </div>
  </div>
);

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const faqs = [
    {
      question: "What is Turcoaz Apps?",
      answer: "Turcoaz Apps is a software company focused on building simple, fast, and privacy-first online tools. Our flagship product, Smart PDF Tools, helps users merge, split, compress, and convert PDF documents directly in the browser—no software installation needed."
    },
    {
      question: "Are the PDF tools free to use?",
      answer: "Yes, our core PDF tools are completely free. You can merge, split, compress, and convert documents without creating an account or paying anything. We believe essential document tools should be accessible to everyone."
    },
    {
      question: "Is it safe to upload my files?",
      answer: "Absolutely. Security is our top priority. All file transfers are encrypted using TLS, and your documents are processed in isolated, secure environments. We never access, read, or analyze your file contents for any purpose other than providing the service you requested."
    },
    {
      question: "Do you store uploaded documents?",
      answer: "No, we do not store your files. All uploaded documents are automatically and permanently deleted immediately after processing is complete. We maintain no copies, backups, or archives of your files. Once you download your result, your original files no longer exist on our servers."
    },
    {
      question: "How do I use the PDF tools?",
      answer: "It's simple: visit Smart PDF Tools, select the tool you need (merge, split, compress, etc.), upload your files, and click process. Your result is ready to download in seconds. No account required, no software to install—everything works directly in your browser."
    }
  ];

  return (
    <section className="py-20 sm:py-24 bg-slate-50" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <p 
            className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            FAQ
          </p>
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Frequently Asked Questions
          </h2>
          <p className="text-base text-slate-600">
            Quick answers to common questions about our online PDF tools and services.
          </p>
        </div>

        <div className="bg-white rounded-xl border border-slate-200/60 shadow-sm px-6">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
import { SEO } from '../components/SEO';

// FAQ structured data for Google rich results
const faqStructuredData = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is Turcoaz Apps?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Turcoaz Apps is a software company focused on building simple, fast, and privacy-first online tools. Our flagship product, Smart PDF Tools, helps users merge, split, compress, and convert PDF documents directly in the browser—no software installation needed."
      }
    },
    {
      "@type": "Question",
      "name": "Are the PDF tools free to use?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, our core PDF tools are completely free. You can merge, split, compress, and convert documents without creating an account or paying anything. We believe essential document tools should be accessible to everyone."
      }
    },
    {
      "@type": "Question",
      "name": "Is it safe to upload my files?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Absolutely. Security is our top priority. All file transfers are encrypted using TLS, and your documents are processed in isolated, secure environments. We never access, read, or analyze your file contents for any purpose other than providing the service you requested."
      }
    },
    {
      "@type": "Question",
      "name": "Do you store uploaded documents?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "No, we do not store your files. All uploaded documents are automatically and permanently deleted immediately after processing is complete. We maintain no copies, backups, or archives of your files. Once you download your result, your original files no longer exist on our servers."
      }
    },
    {
      "@type": "Question",
      "name": "How do I use the PDF tools?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "It's simple: visit Smart PDF Tools, select the tool you need (merge, split, compress, etc.), upload your files, and click process. Your result is ready to download in seconds. No account required, no software to install—everything works directly in your browser."
      }
    }
  ]
};

export const Home = () => {
  return (
    <div data-testid="home-page">
      <SEO 
        title="Turcoaz Apps | Free PDF Tools & AI Document Solutions"
        description="Merge, compress, and convert PDFs for free with Turcoaz Apps. Secure processing, no file storage, and AI-powered tools to simplify your document workflows."
        canonical="/"
      />
      
      {/* FAQ Structured Data for Google Rich Results */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />
      {/* Hero Section */}
      <section 
        className="relative min-h-[90vh] flex items-center justify-center overflow-hidden"
        data-testid="hero-section"
      >
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ 
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/dba5e927-86bf-4b7f-b64d-cbbfd7fa43ce/images/6428d4be469df222cedc8280f61c22a3e2e753259ac250c56be7a7c852b7331f.png')` 
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white via-white/95 to-teal-50/30" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-center max-w-4xl mx-auto">
            <p 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Welcome to Turcoaz Apps
            </p>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none font-semibold text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Free Online PDF Tools{' '}
              <span className="text-teal-600">& AI Solutions</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-10 max-w-2xl mx-auto">
              Merge, split, compress, and convert documents instantly. Secure processing with no file storage—simple tools that just work.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://pdf.turcoazapps.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-3.5 rounded-md text-base font-medium hover:bg-slate-800 transition-colors"
                data-testid="hero-cta-button"
              >
                Explore Smart PDF Tools
                <ExternalLink className="w-5 h-5" />
              </a>
              <a
                href="#products"
                className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-8 py-3.5 rounded-md text-base font-medium hover:bg-teal-100 transition-colors"
                data-testid="hero-secondary-button"
              >
                Learn More
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 sm:py-24 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-slate-900 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Why Choose Turcoaz Apps
            </h2>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              We believe in creating software that respects your time and data.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Zap,
                title: 'Lightning Fast',
                description: 'Our tools are optimized for speed. No bloat, no waiting—just instant results.',
              },
              {
                icon: Shield,
                title: 'Privacy First',
                description: 'Your files stay yours. We process everything securely with your privacy in mind.',
              },
              {
                icon: FileText,
                title: 'Simple & Powerful',
                description: 'Professional-grade features wrapped in an interface anyone can use.',
              },
            ].map((feature, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200/60 rounded-xl shadow-sm p-6 sm:p-8 hover:shadow-md transition-shadow"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-5">
                  <feature.icon className="w-6 h-6 text-teal-600" />
                </div>
                <h3 
                  className="text-xl font-medium text-slate-900 mb-3"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Section */}
      <section 
        id="products" 
        className="py-20 sm:py-24 lg:py-32 bg-slate-50"
        data-testid="product-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
            {/* Content */}
            <div>
              <p 
                className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Our Flagship Product
              </p>
              <h2 
                className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-slate-900 mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Smart PDF Tools
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                A comprehensive suite of PDF tools designed for professionals and everyday users. Merge, split, compress, convert, and manage your PDF documents with ease.
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  'Merge multiple PDFs into one document',
                  'Split PDFs by page or range',
                  'Compress files without losing quality',
                  'Convert between formats seamlessly',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center mt-0.5 flex-shrink-0">
                      <div className="w-2 h-2 rounded-full bg-teal-600" />
                    </div>
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>
              <a
                href="https://pdf.turcoazapps.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-3.5 rounded-md text-base font-medium hover:bg-slate-800 transition-colors"
                data-testid="product-cta-button"
              >
                Open Smart PDF Tools
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
            
            {/* Image */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-100/50 to-transparent rounded-2xl" />
              <img
                src="https://static.prod-images.emergentagent.com/jobs/dba5e927-86bf-4b7f-b64d-cbbfd7fa43ce/images/68728097afd7bd08b849dada47a9ad726d37e2f83049a337d4a0ec855a62a92b.png"
                alt="Smart PDF Tools - Document Management"
                className="w-full rounded-2xl shadow-lg"
                data-testid="product-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Learn & Resources Section */}
      <section className="py-20 sm:py-24 bg-white" data-testid="resources-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p 
              className="text-xs tracking-[0.2em] uppercase font-semibold text-teal-700 mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Learn & Resources
            </p>
            <h2 
              className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              PDF Guides & Tutorials
            </h2>
            <div className="text-slate-600 leading-relaxed space-y-4 text-left sm:text-center">
              <p>
                Working with PDFs doesn't have to be complicated. Whether you need to merge documents for a presentation, compress files for email, or learn how to edit PDF content without expensive software, our free guides walk you through every step.
              </p>
              <p>
                At Turcoaz Apps, we believe everyone should have access to powerful <strong>online PDF tools</strong> and the knowledge to use them effectively. That's why we've created a library of practical tutorials covering everything from basic tasks to advanced document workflows. Explore our full collection of guides on our <Link to="/blog" className="text-teal-600 hover:text-teal-700 font-medium">Blog</Link> for even more tips and tutorials.
              </p>
              <p>
                Browse our <strong>free PDF tools</strong> guides below and discover how to work smarter with your documents. Each tutorial is written in plain language with real examples—no technical jargon, just clear instructions you can follow in minutes.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: 'Best Free PDF Tools Online in 2026',
                description: 'Discover the top free online PDF tools for merging, splitting, compressing, and converting documents. Compare features, security, and ease of use.',
                link: '/blog/best-free-pdf-tools-2026',
              },
              {
                title: 'How to Convert PDF to Word Without Losing Formatting',
                description: 'Learn how to convert PDF files to editable Word documents while preserving layout, fonts, and formatting.',
                link: '/blog/convert-pdf-to-word-without-losing-formatting',
              },
              {
                title: 'How AI Extracts Data from Invoices Automatically',
                description: 'Learn how AI-powered document processing extracts data from invoices automatically using OCR, NLP, and machine learning.',
                link: '/blog/ai-extract-data-from-invoices',
              },
            ].map((article, index) => (
              <Link
                key={index}
                to={article.link}
                className="group bg-white border border-slate-200/60 rounded-xl p-6 hover:shadow-lg hover:border-teal-200 transition-all duration-200"
                data-testid={`resource-card-${index}`}
              >
                <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-100 transition-colors">
                  <BookOpen className="w-5 h-5 text-teal-600" />
                </div>
                <h3 
                  className="text-lg font-medium text-slate-900 mb-2 group-hover:text-teal-700 transition-colors"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {article.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  {article.description}
                </p>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-teal-600 group-hover:text-teal-700">
                  Read article
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            ))}
          </div>

          <div className="text-center mt-10">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium transition-colors"
              data-testid="view-all-resources-link"
            >
              View all articles
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection />

      {/* CTA Section */}
      <section className="py-20 sm:py-24 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight font-medium text-white mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Ready to Simplify Your Workflow?
          </h2>
          <p className="text-lg text-slate-300 mb-10 max-w-2xl mx-auto">
            Start using Smart PDF Tools today. No signup required for basic features.
          </p>
          <a
            href="https://pdf.turcoazapps.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white text-slate-900 px-8 py-3.5 rounded-md text-base font-medium hover:bg-slate-100 transition-colors"
            data-testid="bottom-cta-button"
          >
            Get Started Free
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>
      </section>
    </div>
  );
};
